import { useState, useMemo, useEffect } from 'react';
import { useAuth } from '../../../hooks/useAuth';
import { supabase } from '../../../lib/supabase';
import { mockTeamMembers, TeamMember } from '../../../mocks/step6Data';

const CURRENT_DATE = new Date('2026-03-25');

function daysSince(dateStr: string) {
  return Math.floor((CURRENT_DATE.getTime() - new Date(dateStr).getTime()) / 86400000);
}

type RiskLevel = 'critical' | 'high' | 'medium' | 'low';
type FilterLevel = 'tumu' | RiskLevel;

interface RiskMember {
  member: TeamMember;
  isL1: boolean;
  days: number;
  score: number;
  level: RiskLevel;
  tags: string[];
}

interface ActionItem {
  label: string;
  message: string;
  icon: string;
  color: string;
}

function calcRiskScore(member: TeamMember, isL1: boolean, days: number): number {
  let score = 0;
  if (days > 20) score += 40;
  else if (days > 14) score += 25;
  else if (days > 7) score += 10;
  if (member.status === 'pasif') score += 30;
  else if (member.status === 'ilerliyor') score += 10;
  if (!member.step1Completed) score += 15;
  if (isL1 && (member.ownTeam?.length || 0) === 0) score += 10;
  return Math.min(100, score);
}

function getRiskLevel(score: number): RiskLevel {
  if (score >= 70) return 'critical';
  if (score >= 45) return 'high';
  if (score >= 20) return 'medium';
  return 'low';
}

function getRiskTags(member: TeamMember, isL1: boolean, days: number): string[] {
  const tags: string[] = [];
  if (days > 14) tags.push('İletişim Koptu');
  if (member.status === 'pasif') tags.push('Pasif Durum');
  if (!member.step1Completed) tags.push('1. Adım Eksik');
  if (isL1 && (member.ownTeam?.length || 0) === 0) tags.push('Ekip Boş');
  if (member.status === 'ilerliyor' && days > 5) tags.push('Destek Gerekli');
  return tags;
}

const ACTION_MAP: Record<string, ActionItem[]> = {
  'İletişim Koptu': [
    { label: 'Nasılsın mesajı gönder', icon: 'ri-heart-line', color: 'text-rose-400', message: 'Merhaba {isim}! Bir süredir haberleşemedik, nasılsın? Seni düşündüm, her şey yolunda umarım! 😊' },
    { label: 'Tecrübe paylaşımı yap', icon: 'ri-fire-line', color: 'text-orange-400', message: 'Merhaba {isim}! Bu hafta sistemde güzel gelişmeler var, seninle paylaşmak istedim. Uygun bir an var mı? 🔥' },
  ],
  'Pasif Durum': [
    { label: 'Motivasyon mesajı at', icon: 'ri-rocket-2-line', color: 'text-amber-400', message: 'Merhaba {isim}! Bazen motivasyon düşüyor, bu çok normal. Ama küçük adımlar büyük değişimler yaratır. Bir adım atmaya hazır mısın? 💪' },
    { label: 'Aktiflik hatırlatması', icon: 'ri-alarm-warning-line', color: 'text-yellow-400', message: 'Merhaba {isim}! Bu ay aktifliğini henüz tamamlamadın. Aktif kalmak çok önemli, yardımcı olmamı ister misin? 🙏' },
  ],
  '1. Adım Eksik': [
    { label: '1. Adım anlatım mesajı', icon: 'ri-footprint-line', color: 'text-sky-400', message: 'Merhaba {isim}! 1. adım için seninle konuşmak istiyorum. Kısaca: aylık ürün alımı + 4 kişi ekip. Şimdi başlasak nasıl olur? 🚀' },
    { label: 'Görüşme ayarla', icon: 'ri-calendar-line', color: 'text-purple-400', message: 'Merhaba {isim}! Bu hafta 15 dakikan var mı? 1. adım planını birlikte gözden geçirelim, net bir yol haritası çıkaralım. 📍' },
  ],
  'Ekip Boş': [
    { label: 'Referans iste', icon: 'ri-user-add-line', color: 'text-green-400', message: 'Merhaba {isim}! Sistemden haberdar olup ilgilenebilecek 2-3 kişi tanıyor musun? Onlara da bu fırsatı gösterelim! 🤝' },
    { label: 'Sistem videosunu paylaş', icon: 'ri-play-circle-line', color: 'text-emerald-400', message: 'Merhaba {isim}! Sana sistemimizi anlatan kısa bir video attım. Çevrendeki merak eden biri varsa iletebilirsin! 🎬' },
  ],
  'Destek Gerekli': [
    { label: 'Destek çağrısı yap', icon: 'ri-phone-line', color: 'text-indigo-400', message: 'Merhaba {isim}! Sürecinde nasıl gittiğini merak ettim. Bir engel ya da soru varsa yardımcı olmak isterim. Ne düşünüyorsun? 💬' },
    { label: 'Haftalık takip mesajı', icon: 'ri-calendar-check-line', color: 'text-teal-400', message: 'Merhaba {isim}! Bu haftanın özeti nasıl? İlerleme varsa harika, yoksa birlikte bir plan yapalım. 📊' },
  ],
};

const LEVEL_CONFIG: Record<RiskLevel, { label: string; color: string; bg: string; border: string; barColor: string; dot: string }> = {
  critical: { label: 'Kritik', color: 'text-red-400', bg: 'rgba(239,68,68,0.1)', border: 'rgba(239,68,68,0.22)', barColor: '#EF4444', dot: 'bg-red-500' },
  high: { label: 'Yüksek', color: 'text-orange-400', bg: 'rgba(249,115,22,0.09)', border: 'rgba(249,115,22,0.2)', barColor: '#F97316', dot: 'bg-orange-500' },
  medium: { label: 'Orta', color: 'text-yellow-400', bg: 'rgba(234,179,8,0.08)', border: 'rgba(234,179,8,0.18)', barColor: '#EAB308', dot: 'bg-yellow-500' },
  low: { label: 'Düşük', color: 'text-green-400', bg: 'rgba(34,197,94,0.07)', border: 'rgba(34,197,94,0.15)', barColor: '#22C55E', dot: 'bg-green-500' },
};

interface RiskCardProps {
  rm: RiskMember;
  handled: boolean;
  onToggleHandled: () => void;
}

function RiskCard({ rm, handled, onToggleHandled }: RiskCardProps) {
  const [expanded, setExpanded] = useState(false);
  const [copiedIdx, setCopiedIdx] = useState<number | null>(null);
  const cfg = LEVEL_CONFIG[rm.level];
  const actions = rm.tags.flatMap(tag => ACTION_MAP[tag] || []);

  const handleCopy = (msg: string, idx: number) => {
    const text = msg.replace(/\{isim\}/g, rm.member.name.split(' ')[0]);
    navigator.clipboard.writeText(text).catch(() => {});
    setCopiedIdx(idx);
    setTimeout(() => setCopiedIdx(null), 2000);
  };

  return (
    <div className="rounded-2xl overflow-hidden transition-all duration-300" style={{ border: `1px solid ${cfg.border}`, opacity: handled ? 0.45 : 1 }}>
      {/* Main row */}
      <div className="flex items-center gap-3 p-4" style={{ background: cfg.bg }}>
        <div className="w-10 h-10 rounded-full flex items-center justify-center text-sm font-bold text-white flex-shrink-0"
          style={{ background: 'linear-gradient(135deg, #6B7280 0%, #374151 100%)' }}>
          {rm.member.name.charAt(0)}
        </div>

        <div className="flex-1 min-w-0">
          <div className="flex items-center gap-2 flex-wrap mb-1.5">
            <span className="text-sm font-bold text-white">{rm.member.name}</span>
            <span className={`text-xs font-bold px-2 py-0.5 rounded-full ${cfg.color}`} style={{ background: 'rgba(0,0,0,0.3)' }}>
              {cfg.label} Risk
            </span>
            <span className={`text-xs px-2 py-0.5 rounded-full font-medium ${rm.isL1 ? 'text-amber-400' : 'text-sky-400'}`}
              style={{ background: rm.isL1 ? 'rgba(245,166,35,0.12)' : 'rgba(56,189,248,0.12)' }}>
              {rm.isL1 ? 'L1 — Direkt Ekip' : 'L2 — Alt Ekip'}
            </span>
          </div>
          <div className="flex flex-wrap gap-1.5">
            {rm.tags.map(tag => (
              <span key={tag} className="text-xs px-2 py-0.5 rounded-md font-medium text-gray-300" style={{ background: 'rgba(255,255,255,0.08)' }}>
                {tag}
              </span>
            ))}
            {rm.tags.length === 0 && (
              <span className="text-xs text-gray-600 italic">Belirgin risk faktörü yok</span>
            )}
          </div>
        </div>

        {/* Score gauge */}
        <div className="text-right flex-shrink-0 mr-1">
          <div className={`text-2xl font-black leading-none ${cfg.color}`}>{rm.score}</div>
          <div className="text-xs text-gray-600 mt-0.5">/ 100</div>
        </div>

        {/* Buttons */}
        <div className="flex flex-col gap-1.5 flex-shrink-0">
          <button onClick={() => setExpanded(!expanded)} title="Aksiyon önerilerini gör"
            className="w-8 h-8 rounded-lg flex items-center justify-center cursor-pointer transition-all"
            style={{ background: expanded ? 'rgba(245,166,35,0.2)' : 'rgba(255,255,255,0.07)' }}>
            <i className={`${expanded ? 'ri-lightbulb-fill text-amber-400' : 'ri-lightbulb-line text-gray-400'} text-sm`} />
          </button>
          <button onClick={onToggleHandled} title={handled ? 'İşareti kaldır' : 'Ele alındı'}
            className="w-8 h-8 rounded-lg flex items-center justify-center cursor-pointer transition-all"
            style={{ background: handled ? 'rgba(34,197,94,0.18)' : 'rgba(255,255,255,0.07)' }}>
            <i className={`ri-check-line text-sm ${handled ? 'text-green-400' : 'text-gray-500'}`} />
          </button>
        </div>
      </div>

      {/* Score bar */}
      <div className="h-1" style={{ background: 'rgba(255,255,255,0.04)' }}>
        <div className="h-full transition-all duration-700" style={{ width: `${rm.score}%`, background: cfg.barColor }} />
      </div>

      {/* Meta info */}
      <div className="flex items-center flex-wrap gap-x-4 gap-y-1 px-4 py-2.5" style={{ background: '#0D0F18' }}>
        <span className="text-xs text-gray-500 flex items-center gap-1.5">
          <i className="ri-time-line text-gray-600 text-xs" />
          <span>Son iletişim:</span>
          <strong className={rm.days > 14 ? 'text-red-400' : rm.days > 7 ? 'text-yellow-400' : 'text-gray-400'}>
            {rm.days} gün önce
          </strong>
        </span>
        <span className="text-xs text-gray-500 flex items-center gap-1.5">
          <i className="ri-phone-line text-gray-600 text-xs" />
          {rm.member.phone}
        </span>
        <div className="ml-auto">
          <a href={`https://wa.me/${rm.member.phone.replace(/\D/g, '')}`} target="_blank" rel="noopener noreferrer"
            className="flex items-center gap-1.5 px-3 py-1 rounded-lg text-xs font-semibold text-white cursor-pointer transition-all hover:opacity-80 whitespace-nowrap"
            style={{ background: 'linear-gradient(135deg, #25D366 0%, #128C7E 100%)' }}>
            <i className="ri-whatsapp-line text-xs" />
            WhatsApp Aç
          </a>
        </div>
      </div>

      {/* Expanded: Action Suggestions */}
      {expanded && (
        <div className="px-4 py-4 space-y-2 border-t" style={{ borderColor: 'rgba(255,255,255,0.06)', background: '#0A0C14' }}>
          <p className="text-xs font-semibold text-amber-400 flex items-center gap-1.5 mb-3">
            <i className="ri-lightbulb-line" />
            {rm.member.name.split(' ')[0]} için önerilen aksiyonlar
          </p>
          {actions.length > 0 ? actions.map((action, idx) => (
            <div key={idx} className="flex items-center gap-3 p-3 rounded-xl"
              style={{ background: 'rgba(255,255,255,0.03)', border: '1px solid rgba(255,255,255,0.05)' }}>
              <div className="w-7 h-7 flex items-center justify-center flex-shrink-0">
                <i className={`${action.icon} text-sm ${action.color}`} />
              </div>
              <span className="flex-1 text-xs text-gray-300 min-w-0">{action.label}</span>
              <button onClick={() => handleCopy(action.message, idx)}
                className="flex items-center gap-1.5 px-3 py-1.5 rounded-lg text-xs font-medium cursor-pointer transition-all whitespace-nowrap flex-shrink-0"
                style={copiedIdx === idx
                  ? { background: 'rgba(34,197,94,0.15)', color: '#4ADE80', border: '1px solid rgba(34,197,94,0.2)' }
                  : { background: 'rgba(255,255,255,0.06)', color: '#9CA3AF', border: '1px solid rgba(255,255,255,0.06)' }}>
                <i className={`${copiedIdx === idx ? 'ri-check-line' : 'ri-file-copy-line'} text-xs`} />
                {copiedIdx === idx ? 'Kopyalandı!' : 'Mesajı Kopyala'}
              </button>
            </div>
          )) : (
            <p className="text-xs text-gray-600 italic py-1">Bu kişi için özel aksiyon yok — durum iyi görünüyor.</p>
          )}
        </div>
      )}
    </div>
  );
}

export default function RiskMerkezi() {
  const { profile } = useAuth();
  const [teamData, setTeamData] = useState<TeamMember[]>(mockTeamMembers);
  const [dataLoading, setDataLoading] = useState(true);

  useEffect(() => {
    if (!profile) { setDataLoading(false); return; }
    let cancelled = false;
    supabase.from('step6_team_members').select('*').eq('user_id', profile.id).order('sort_order', { ascending: true })
      .then(({ data }) => {
        if (cancelled) return;
        if (data && data.length > 0) {
          const l1Members = data.filter((m: Record<string, unknown>) => !m.parent_id);
          const l2Members = data.filter((m: Record<string, unknown>) => m.parent_id);
          setTeamData(l1Members.map((l1: Record<string, unknown>) => ({
            id: l1.id as string, name: l1.name as string, phone: l1.phone as string,
            status: l1.status as TeamMember['status'], step1Completed: l1.step1_completed as boolean,
            lastContact: l1.last_contact as string, note: l1.note as string, joinDate: l1.join_date as string,
            ownTeam: l2Members.filter((l2: Record<string, unknown>) => l2.parent_id === l1.id).map((l2: Record<string, unknown>) => ({
              id: l2.id as string, name: l2.name as string, phone: l2.phone as string,
              status: l2.status as TeamMember['status'], step1Completed: l2.step1_completed as boolean,
              lastContact: l2.last_contact as string, note: l2.note as string, joinDate: l2.join_date as string,
            })),
          })));
        }
        setDataLoading(false);
      });
    return () => { cancelled = true; };
  }, [profile]);

  const l1 = teamData;
  const l2 = teamData.flatMap(m => m.ownTeam || []);

  const [filterLevel, setFilterLevel] = useState<FilterLevel>('tumu');
  const [handledIds, setHandledIds] = useState<Set<string>>(new Set());
  const [showHandled, setShowHandled] = useState(false);

  const riskMembers: RiskMember[] = useMemo(() => {
    const all = [
      ...l1.map(m => ({ member: m, isL1: true })),
      ...l2.map(m => ({ member: m, isL1: false })),
    ];
    return all.map(({ member, isL1 }) => {
      const days = daysSince(member.lastContact);
      const score = calcRiskScore(member, isL1, days);
      return { member, isL1, days, score, level: getRiskLevel(score), tags: getRiskTags(member, isL1, days) };
    }).sort((a, b) => b.score - a.score);
  }, [l1, l2]);

  const levelCounts = useMemo(() => ({
    critical: riskMembers.filter(r => r.level === 'critical').length,
    high: riskMembers.filter(r => r.level === 'high').length,
    medium: riskMembers.filter(r => r.level === 'medium').length,
    low: riskMembers.filter(r => r.level === 'low').length,
  }), [riskMembers]);

  const visibleMembers = riskMembers.filter(rm =>
    (filterLevel === 'tumu' || rm.level === filterLevel) &&
    (showHandled || !handledIds.has(rm.member.id))
  );

  const toggleHandled = (id: string) => setHandledIds(prev => {
    const next = new Set(prev);
    next.has(id) ? next.delete(id) : next.add(id);
    return next;
  });

  return (
    <div className="space-y-6">
      <div className="flex items-center justify-between flex-wrap gap-4">
        <div>
          <h1 className="text-2xl font-bold text-white">Risk Merkezi</h1>
          <p className="text-sm text-gray-500 mt-1">Otomatik risk skoru, öncelik sıralaması ve kişi bazlı aksiyon önerileri</p>
        </div>
        <button onClick={() => setShowHandled(v => !v)}
          className="flex items-center gap-2 px-4 py-2 rounded-xl text-xs font-semibold cursor-pointer transition-all whitespace-nowrap"
          style={showHandled
            ? { background: 'rgba(34,197,94,0.15)', color: '#4ADE80', border: '1px solid rgba(34,197,94,0.25)' }
            : { background: 'rgba(255,255,255,0.06)', color: '#9CA3AF', border: '1px solid rgba(255,255,255,0.08)' }}>
          <i className={`${showHandled ? 'ri-eye-fill' : 'ri-eye-off-line'} text-sm`} />
          {showHandled ? 'Ele Alınanlar Görünüyor' : 'Ele Alınanları Göster'}
        </button>
      </div>

      {/* Priority summary cards — clickable filters */}
      <div className="grid grid-cols-2 lg:grid-cols-4 gap-3">
        {(['critical', 'high', 'medium', 'low'] as const).map(level => {
          const cfg = LEVEL_CONFIG[level];
          const active = filterLevel === level;
          return (
            <button key={level} onClick={() => setFilterLevel(active ? 'tumu' : level)}
              className="rounded-2xl p-4 text-left cursor-pointer transition-all hover:scale-[1.02]"
              style={{ background: active ? cfg.bg : 'rgba(255,255,255,0.03)', border: `1px solid ${active ? cfg.border : 'rgba(255,255,255,0.06)'}` }}>
              <div className="flex items-center gap-2 mb-2">
                <span className={`w-2.5 h-2.5 rounded-full ${cfg.dot}`} />
                <span className={`text-xs font-semibold ${cfg.color}`}>{cfg.label}</span>
              </div>
              <div className={`text-3xl font-black ${cfg.color}`}>{levelCounts[level]}</div>
              <div className="text-xs text-gray-600 mt-0.5">kişi</div>
            </button>
          );
        })}
      </div>

      {/* How scoring works */}
      <div className="rounded-xl p-3.5 flex items-start gap-3" style={{ background: 'rgba(245,166,35,0.05)', border: '1px solid rgba(245,166,35,0.12)' }}>
        <i className="ri-information-line text-amber-400 text-sm flex-shrink-0 mt-0.5" />
        <p className="text-xs text-gray-400 leading-relaxed">
          Risk puanı; <strong className="text-amber-400/80">son iletişim süresi</strong> (en yüksek ağırlık), <strong className="text-amber-400/80">pasif durum</strong>, <strong className="text-amber-400/80">1. adım eksikliği</strong> ve <strong className="text-amber-400/80">ekip boşluğu</strong> faktörlerine göre otomatik hesaplanır ve sıraya dizilir.
          Ampul ikonuna tıklayarak o kişiye özel hazır WhatsApp mesaj önerilerini kopyalayabilirsin.
        </p>
      </div>

      {/* Filter tabs */}
      <div className="flex gap-1 p-1 rounded-xl w-fit" style={{ background: 'rgba(255,255,255,0.04)', border: '1px solid rgba(255,255,255,0.06)' }}>
        {(['tumu', 'critical', 'high', 'medium', 'low'] as const).map(level => (
          <button key={level} onClick={() => setFilterLevel(level)}
            className="px-3 py-1.5 rounded-lg text-xs font-semibold cursor-pointer transition-all whitespace-nowrap"
            style={filterLevel === level ? { background: 'rgba(245,166,35,0.2)', color: '#F5A623' } : { color: '#6B7280' }}>
            {level === 'tumu' ? `Tümü (${riskMembers.length})` : `${LEVEL_CONFIG[level].label} (${levelCounts[level]})`}
          </button>
        ))}
      </div>

      {/* Risk list */}
      <div className="space-y-3">
        {visibleMembers.length > 0 ? (
          visibleMembers.map(rm => (
            <RiskCard key={rm.member.id} rm={rm} handled={handledIds.has(rm.member.id)} onToggleHandled={() => toggleHandled(rm.member.id)} />
          ))
        ) : (
          <div className="rounded-2xl p-12 flex flex-col items-center justify-center gap-3"
            style={{ background: '#12141E', border: '1px solid rgba(255,255,255,0.06)' }}>
            <div className="w-14 h-14 rounded-full flex items-center justify-center" style={{ background: 'rgba(34,197,94,0.1)' }}>
              <i className="ri-shield-check-line text-green-400 text-2xl" />
            </div>
            <p className="text-sm font-semibold text-white">Bu kategoride risk yok</p>
            <p className="text-xs text-gray-600 text-center">Harika! Bu risk seviyesinde dikkat gerektiren kişi görünmüyor.</p>
          </div>
        )}
      </div>

      {/* Handled count banner */}
      {handledIds.size > 0 && !showHandled && (
        <div className="rounded-xl p-3 flex items-center justify-between gap-3"
          style={{ background: 'rgba(34,197,94,0.06)', border: '1px solid rgba(34,197,94,0.15)' }}>
          <div className="flex items-center gap-2">
            <i className="ri-check-double-line text-green-400 text-sm" />
            <span className="text-xs text-green-400 font-medium">
              {handledIds.size} kişi ele alındı olarak işaretlendi
            </span>
          </div>
          <button onClick={() => setShowHandled(true)} className="text-xs text-gray-500 cursor-pointer hover:text-gray-300 whitespace-nowrap transition-colors">
            Görüntüle
          </button>
        </div>
      )}
    </div>
  );
}